import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthData, UserRoles } from '@/types/api/auth';
import { api } from '@/lib/api';
import { clearAllCaches } from '@/lib/cacheCleanup';
import { isImpersonatingNow } from '@/lib/impersonation';

const STORE_KEY = 'user-storage';
/** Records the user's "Remember password" choice: 'local' | 'session'. */
const REMEMBER_KEY = 'auth-persistence';

/**
 * Which Storage backs the session right now.
 *
 * - Impersonation always wins: that tab is backed by sessionStorage so the
 *   original super-admin's tab (on localStorage) isn't overwritten, and closing
 *   the tab ends the impersonation.
 * - Otherwise the login form's "Remember password" choice decides. Unchecked
 *   means sessionStorage, so closing the browser signs the user out — which is
 *   what the label has always promised but never delivered.
 */
const resolveStorage = (): Storage => {
  if (isImpersonatingNow()) return sessionStorage;
  try {
    return localStorage.getItem(REMEMBER_KEY) === 'session' ? sessionStorage : localStorage;
  } catch {
    return localStorage;
  }
};

/**
 * Records the choice and migrates any existing session to the chosen backend.
 * Call this BEFORE `login()` so the first persist write lands in the right place.
 */
export const setSessionPersistence = (remember: boolean): void => {
  if (isImpersonatingNow()) return;
  try {
    localStorage.setItem(REMEMBER_KEY, remember ? 'local' : 'session');
    const from = remember ? sessionStorage : localStorage;
    const to = remember ? localStorage : sessionStorage;
    const existing = from.getItem(STORE_KEY);
    if (existing !== null) to.setItem(STORE_KEY, existing);
    from.removeItem(STORE_KEY);
  } catch {
    // Storage unavailable (private mode / blocked) — fall back to the default.
  }
};

/** Clears the session from BOTH backends, whichever one is currently active. */
export const clearPersistedSession = (): void => {
  try {
    localStorage.removeItem(STORE_KEY);
    sessionStorage.removeItem(STORE_KEY);
    localStorage.removeItem(REMEMBER_KEY);
  } catch { /* ignore */ }
};

// Proxy: resolved per operation, so the backend can change at login time.
const persistStorage = {
  getItem: (name: string) => resolveStorage().getItem(name),
  setItem: (name: string, value: string) => resolveStorage().setItem(name, value),
  removeItem: (name: string) => resolveStorage().removeItem(name),
};

interface Workspace {
  id: number;
  name: string;
  logo_url?: string | null;
}

interface UserState {
  token: string | null;
  user: AuthData | null;
  role: UserRoles | null;
  isSuperAdmin: boolean;
  isPartner: boolean;
  selectedWorkspaceId: number | null; // For sys admins to select workspace context
  workspaces: Workspace[]; // Available workspaces for super admin
  workspacesLoaded: boolean; // True after bootstrap completes at least once
  workspacesLoadPromise: Promise<void> | null; // In-flight bootstrap (not persisted)

  // Actions
  setRole: (role: UserRoles) => void;
  setSelectedWorkspaceId: (workspaceId: number | null) => void;
  fetchWorkspaces: () => Promise<void>;
  fetchPartnerWorkspaces: () => Promise<void>;
  bootstrapWorkspaces: () => Promise<void>;
  ensureWorkspaceReady: () => Promise<void>;
  login: (authData: AuthData) => void;
  logout: () => void;
  updateProfile: (newProfile: Partial<AuthData['user_profile']>) => void;
  updateSuperAdminAvatarUrl: (url: string | null) => void;
  completeFirstTimeLogin: () => void;
  clearCache: () => void;
}

// Internal bootstrap implementation. Loads the workspace list and picks a default.
// Kept outside the store so it can be reused by both `fetchWorkspaces` (refresh) and
// `bootstrapWorkspaces` (idempotent first-load).
// The workspace switcher needs the COMPLETE list (any workspace must be
// selectable), but the /workspaces endpoint is paginated (default 15). So we
// page through it: show the first page immediately (fast render + default
// pick), then "top up" with the remaining pages until the list is complete.
const WORKSPACE_PAGE_SIZE = 100;
const WORKSPACE_PAGE_CAP = 50; // safety bound: up to 5000 workspaces

type WorkspaceListResponse = {
  data: Workspace[];
  meta?: { last_page?: number };
};

const runWorkspaceLoad = async (
  set: (partial: Partial<UserState>) => void,
  get: () => UserState,
): Promise<void> => {
  try {
    const first = (await api.get(
      `/workspaces?per_page=${WORKSPACE_PAGE_SIZE}&page=1`,
    )) as WorkspaceListResponse;
    let workspaces = first.data ?? [];
    const current = get();

    const next: Partial<UserState> = { workspaces, workspacesLoaded: true };
    if (!current.selectedWorkspaceId && workspaces.length > 0) {
      next.selectedWorkspaceId = workspaces[0].id;
    }
    set(next);

    // Fetch the remaining pages and append them once they arrive.
    const lastPage = Math.min(first.meta?.last_page ?? 1, WORKSPACE_PAGE_CAP);
    if ((first.meta?.last_page ?? 1) > WORKSPACE_PAGE_CAP) {
      console.warn(`[Workspace] more than ${WORKSPACE_PAGE_CAP * WORKSPACE_PAGE_SIZE} workspaces; list truncated.`);
    }
    if (lastPage > 1) {
      const rest = await Promise.all(
        Array.from({ length: lastPage - 1 }, (_, i) =>
          (api.get(`/workspaces?per_page=${WORKSPACE_PAGE_SIZE}&page=${i + 2}`) as Promise<WorkspaceListResponse>)
            .then((r) => r.data ?? []),
        ),
      );
      workspaces = workspaces.concat(...rest);
      set({ workspaces });
    }
  } catch (error) {
    console.error('[Workspace] failed to load workspaces:', error);
    set({ workspaces: [], workspacesLoaded: true });
  }
};

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      role: 'admin',
      isSuperAdmin: false,
      isPartner: false,
      selectedWorkspaceId: null,
      workspaces: [],
      workspacesLoaded: false,
      workspacesLoadPromise: null,

      setRole: (role) => set({ role }),

      setSelectedWorkspaceId: (workspaceId) => set({ selectedWorkspaceId: workspaceId }),

      fetchWorkspaces: async () => {
        // Force refresh. Reuse any in-flight bootstrap to avoid duplicate requests.
        const inFlight = get().workspacesLoadPromise;
        if (inFlight) return inFlight;

        const p = runWorkspaceLoad(set, get).finally(() => {
          set({ workspacesLoadPromise: null });
        });
        set({ workspacesLoadPromise: p });
        return p;
      },

      // Partner workspace list for the Topbar switcher. Sourced from the
      // partner-scoped endpoint (only workspaces the partner owns) rather than
      // the super-admin /workspaces list, so a partner never sees or switches
      // into a workspace they don't manage.
      fetchPartnerWorkspaces: async () => {
        try {
          const res = (await api.get('/referral-leads/workspaces')) as {
            data?: Array<{ id: number; name: string; logo_url?: string | null }>;
          };
          const workspaces = (res.data ?? []).map((w) => ({
            id: w.id,
            name: w.name,
            logo_url: w.logo_url ?? null,
          }));
          const current = get();
          const next: Partial<UserState> = { workspaces, workspacesLoaded: true };
          if (!current.selectedWorkspaceId && workspaces.length > 0) {
            next.selectedWorkspaceId = workspaces[0].id;
          }
          set(next);
        } catch (error) {
          console.error('[Workspace] failed to load partner workspaces:', error);
          set({ workspaces: [], workspacesLoaded: true });
        }
      },

      bootstrapWorkspaces: async () => {
        const state = get();

        // Only super admins need the workspace list. Other roles get workspace_id
        // baked into their auth payload, so there's nothing to load.
        if (!state.isSuperAdmin) return;

        // Already loaded with a valid selection — nothing to do.
        if (state.workspacesLoaded && state.workspaces.length > 0 && state.selectedWorkspaceId) {
          return;
        }

        // Reuse in-flight promise so concurrent callers share one request.
        if (state.workspacesLoadPromise) return state.workspacesLoadPromise;

        const p = runWorkspaceLoad(set, get).finally(() => {
          set({ workspacesLoadPromise: null });
        });
        set({ workspacesLoadPromise: p });
        return p;
      },

      ensureWorkspaceReady: async () => {
        const state = get();
        if (!state.isSuperAdmin) return;
        if (state.selectedWorkspaceId) return;
        await get().bootstrapWorkspaces();
      },

      clearCache: () => {
        clearAllCaches();
        console.log('[Cache] All caches cleared');
      },

      login: (authData) => {
        useUserStore.getState().clearCache();

        const permissions = authData.permissions || [];
        const ADMIN_ACCESS = 'access.admin';
        const BUSINESS_ADMIN_ACCESS = 'access.business_admin';
        const MANAGER_ACCESS = 'access.team_manager';
        const LEARNER_ACCESS = 'access.learner';
        const hasTag = (tagToCheck: string) => permissions.includes(tagToCheck);

        const availableRoles: string[] = [];

        if (authData.is_super_admin) {
             availableRoles.push('super_admin');
        } else {
            if (hasTag(ADMIN_ACCESS)) availableRoles.push('admin');
            if (hasTag(BUSINESS_ADMIN_ACCESS)) availableRoles.push('business_admin');
            if (hasTag(MANAGER_ACCESS)) availableRoles.push('team_manager');
            if (hasTag(LEARNER_ACCESS)) availableRoles.push('learner');
        }

        // Partner is a central flag (independent of workspace role); surface it
        // as an available role so partner-specific UI/menus can gate on it.
        const isPartner = !!authData.is_partner;
        if (isPartner && !availableRoles.includes('partner')) {
            availableRoles.push('partner');
        }

        const updatedAuthData = {
            ...authData,
            roles: availableRoles
        };

        let derivedRole: UserRoles = 'learner';

        if (authData.is_super_admin) {
            derivedRole = 'super_admin';
        } else if (hasTag(BUSINESS_ADMIN_ACCESS)) {
            derivedRole = 'business_admin';
        } else if (hasTag(ADMIN_ACCESS)) {
            derivedRole = 'admin';
        } else if (hasTag(MANAGER_ACCESS)) {
            derivedRole = 'team_manager';
        } else if (hasTag(LEARNER_ACCESS)) {
            derivedRole = 'learner';
        }

        // Same user logging back into the same tab should see the branding
        // reminder again across every workspace they switch into, so wipe both
        // the per-workspace dismissal flags and the session-wide snooze flag
        // scoped to this user id.
        {
          const prefix = `branding-reminder-dismissed:${authData.user_id}:`;
          for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(prefix)) {
              sessionStorage.removeItem(key);
            }
          }
          sessionStorage.removeItem(`branding-reminder-snoozed-all:${authData.user_id}`);
        }

        set({
          token: authData.token,
          user: updatedAuthData,
          role: derivedRole,
          isSuperAdmin: authData.is_super_admin,
          isPartner,
          selectedWorkspaceId: authData.workspace_id,
          // Reset bootstrap state on every login so a fresh session always
          // re-fetches the workspace list (covers users switching accounts).
          workspaces: [],
          workspacesLoaded: false,
          workspacesLoadPromise: null,
        });
      },

      logout: () => {
        const currentToken = useUserStore.getState().token;

        if (currentToken) {
          const baseUrl = import.meta.env.VITE_API_URL;
          fetch(`${baseUrl}/auth/logout`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${currentToken}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          }).catch(() => {});
        }

        useUserStore.getState().clearCache();

        set({
          token: null,
          user: null,
          role: null,
          isSuperAdmin: false,
          isPartner: false,
          selectedWorkspaceId: null,
          workspaces: [],
          workspacesLoaded: false,
          workspacesLoadPromise: null,
        });
        // Clear BOTH backends, not just the currently-resolved one — the
        // remember-me choice may have moved the session between them.
        clearPersistedSession();
      },

      updateProfile: (newProfile) =>
        set((state) => ({
          user: state.user
            ? { ...state.user, user_profile: { ...state.user.user_profile, ...newProfile } as AuthData['user_profile'] }
            : null
        })),

      updateSuperAdminAvatarUrl: (url) =>
        set((state) => ({
          user: state.user
            ? { ...state.user, super_admin_avatar_url: url }
            : null,
        })),

      completeFirstTimeLogin: () =>
        set((state) => ({
          user: state.user ? { ...state.user, is_first_time_login: false } : null,
        })),
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => persistStorage),
      // Persist only serializable state. The in-flight load promise must never
      // be persisted — JSON.stringify turns a Promise into "{}", which would
      // mislead the bootstrap into thinking a request is in flight on rehydrate.
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        role: state.role,
        isSuperAdmin: state.isSuperAdmin,
        isPartner: state.isPartner,
        selectedWorkspaceId: state.selectedWorkspaceId,
        workspaces: state.workspaces,
        workspacesLoaded: state.workspacesLoaded,
      }) as unknown as UserState,
    }
  )
);
